import { useState, useCallback, useEffect } from 'react'
import { useAuth } from '@src/modules/Auth/shared/state'
import { SiteModifySite } from '@src/services/api-client'
import { Alerts } from '@src/components/Alerts'
import { useMemoDebounced } from './useMemoDebounced'

export const useAutoSavePositions = () => {
  const { currentSite, setCurrentSite } = useAuth()
  const [selectedPositions, setSelectedPositions] = useState(
    currentSite?.emailNotificationPositionIds || []
  )
  const [isSaving, setIsSaving] = useState(false)
  
  const debouncedPositions = useMemoDebounced(() => selectedPositions, [selectedPositions], 1000)
  
  const handlePositionChange = useCallback((newPositions: string[]) => {
    setSelectedPositions(newPositions)
  }, [])
  
  const savePositions = useCallback(async (positions: string[]) => {
    if (!currentSite || isSaving) return
    
    setIsSaving(true)
    try {
      await SiteModifySite({
        id: currentSite.id,
        emailNotificationPositionIds: positions,
      })
      // Update currentSite state with new positions
      setCurrentSite({...currentSite, emailNotificationPositionIds: positions})
    } catch (err: any) {
      Alerts.error('Failed to save notification settings', err.message)
    } finally {
      setIsSaving(false)
    }
  }, [currentSite, isSaving, setCurrentSite])
  
  useEffect(() => {
    if (debouncedPositions !== currentSite?.emailNotificationPositionIds) {
      savePositions(debouncedPositions)
    }
  }, [debouncedPositions, currentSite, savePositions])
  
  useEffect(() => {
    setSelectedPositions(currentSite?.emailNotificationPositionIds || [])
  }, [currentSite?.emailNotificationPositionIds])

  return {
    selectedPositions,
    handlePositionChange,
    isSaving,
  }
}
